import { toast } from 'react-toastify'

// Dumb-simple Title Case: capitalize the first letter of each whitespace-separated
// word, lowercase the rest. No small-word exceptions. "rain on GLASS" -> "Rain On Glass".
export function toTitleCase(value) {
  return value.replace(/\S+/g, word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  )
}

// Derive a snake_case token from a display name: lowercase, collapse each run of
// non-alphanumeric characters to a single underscore, strip leading/trailing
// underscores, then strip any leading non-letter characters — the backend token
// regex requires a leading letter, so a digit-leading token (e.g. "22 Hearts")
// would be rejected AFTER the upload. "22 Hearts" -> "hearts"; "Floating Hearts!"
// -> "floating_hearts". Returns '' when nothing usable remains (invalid).
export function deriveToken(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
}

// Client-side cap. The server is the source of truth on dims/duration/frame count;
// these are just fast local guards + operator guidance.
export const MAX_WEBP_BYTES = 10 * 1024 * 1024 // ~10MB

// Compact, always-visible requirements copy shown above the file picker.
export const WEBP_REQUIREMENTS_COPY =
  'WebP requirements — Animated WebP (.webp) only · must contain 2+ frames · ' +
  'portrait 9:16, 540×960 recommended (1080×1920 ok) · ~2s seamless loop, 30fps · ' +
  'transparency supported · 10MB max. Stills are rejected. The server reads ' +
  'dimensions and duration from the file itself.'

// Canned prompt an admin can hand to an LLM to author a valid import zip. The
// inline // comments are intentional — they are guidance FOR the LLM, part of the
// verbatim prompt text, not JS comments.
export const LLM_MANIFEST_PROMPT = `Create a zip file of animated overlay filters for a short-form music video app. The zip must contain:

1. One or more animated WebP files (.webp). Each must be:
- ANIMATED WebP (2+ frames) — still images are rejected
- portrait 9:16, 540x960 pixels (1080x1920 also accepted)
- a seamless loop of about 2 seconds at ~30fps (the last frame must flow into the first)
- transparent background unless the effect is a full-frame tint
- 10MB maximum per file
- a fullscreen ambient overlay effect (particles, weather, glows) that reads well layered over arbitrary user video; glowing effects need enough opacity to survive bright footage

2. Exactly one manifest.json at the zip root, in this schema:
{
  "filters": [
    {
      "file": "rain_on_glass.webp",   // exact filename of a .webp in this zip
      "name": "Rain on Glass",        // display name, Title Case
      "tags": ["heartbreak", "sad"],  // lowercase strings; what the filter is about
      "mood": ["sad", "longing"],     // lowercase strings; the emotions it conveys
      "loop": true                     // true = repeats for the whole video (almost always true)
    }
  ]
}

Rules: every .webp in the zip must have exactly one manifest entry and vice versa; "file" must match the filename exactly; "name" is required and unique within the zip (a lowercase snake_case token is derived from it, e.g. "Rain on Glass" -> rain_on_glass); tags and mood are arrays of short lowercase strings; do not include any other files in the zip.`

// After finalize returns real dims, warn (non-blocking) if the asset isn't 9:16
// within a small tolerance. The upload is still allowed.
export function warnIfNotPortrait916(width, height) {
  if (!width || !height) return
  const target = 9 / 16
  const ratio = width / height
  if (Math.abs(ratio - target) / target > 0.02) {
    toast.warn(
      `Uploaded asset is ${width}×${height} — not 9:16, may crop oddly in the app`
    )
  }
}
